import { useCallback, useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';

import { TwoStepOtpStep, IOtpRequestFields, IOtpVerifyFields } from './two-step-otp-form.types';

// Cooldown applies to both the initial send (which already flipped us to the
// verify step) and every subsequent resend press; ticked by the effect below.
const RESEND_COOLDOWN_SECONDS = 30;

// Cooldown is keyed by destination and persisted so it survives Back, reload,
// and unmount. See CLAUDE.md → "Parent OTP — resend cooldown survives navigation".
const COOLDOWN_KEY = (destination: string) => `mea_otp_cooldown_${destination}`;

const readLastSent = (destination: string): number | null => {
  if (!destination) return null;
  try {
    const raw = window.localStorage.getItem(COOLDOWN_KEY(destination));
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (Date.now() - n >= RESEND_COOLDOWN_SECONDS * 1000) {
      // Expired — clean up so storage doesn't grow unbounded over time.
      window.localStorage.removeItem(COOLDOWN_KEY(destination));
      return null;
    }
    return n;
  } catch {
    return null;
  }
};

const writeLastSent = (destination: string, sentAt: number) => {
  if (!destination) return;
  try { window.localStorage.setItem(COOLDOWN_KEY(destination), String(sentAt)); }
  catch { /* private mode / quota exceeded — cooldown just falls back to in-memory */ }
};

// The server's 429 resend rate-limit surfaces as an AuthenticationError whose
// message is the only stable client signal (a dedicated code would be cleaner).
const isRateLimited = (err: any): boolean => {
  const code = err?.graphQLErrors?.[0]?.extensions?.code ?? err?.extensions?.code;
  if (code === 'RATE_LIMITED' || code === 'TOO_MANY_REQUESTS') return true;
  return /too many requests/i.test(String(err?.message || ''));
};

interface UseTwoStepOtpArgs {
  onRequest: (destination: string) => Promise<void>;
  onVerify: (destination: string, code: string) => Promise<void>;
  requestErrorFallback?: string;
  verifyErrorFallback?: string;
}

interface UseTwoStepOtpReturn {
  step: TwoStepOtpStep;
  destination: string;
  submitError: string | null;
  handleRequest: (values: IOtpRequestFields, helpers: FormikHelpers<IOtpRequestFields>) => Promise<void>;
  handleVerify: (values: IOtpVerifyFields, helpers: FormikHelpers<IOtpVerifyFields>) => Promise<void>;
  onBack: () => void;
  // Resend: 0 = enabled. Otherwise seconds remaining before the user can press again.
  resendSecondsRemaining: number;
  resending: boolean;
  handleResend: () => Promise<void>;
}

// HOOK: drives the two-step OTP state machine; consumers wire onRequest/onVerify
// to their flow (login → setAuthUser+push; in-session → refetch).
export const useTwoStepOtp = ({
  onRequest,
  onVerify,
  requestErrorFallback = 'Could not send a code. Please try again.',
  verifyErrorFallback = 'That code was invalid. Please try again.',
}: UseTwoStepOtpArgs): UseTwoStepOtpReturn => {
  const [step, setStep] = useState<TwoStepOtpStep>('request');
  const [destination, setDestination] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const [resendSecondsRemaining, setResendSecondsRemaining] = useState(0);
  const [resending, setResending] = useState(false);

  // Tick the countdown every second while a cooldown is active.
  useEffect(() => {
    if (!lastSentAt) {
      setResendSecondsRemaining(0);
      return undefined;
    }
    const recompute = () => {
      const elapsed = Math.floor((Date.now() - lastSentAt) / 1000);
      const remaining = Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
      setResendSecondsRemaining(remaining);
      return remaining;
    };
    if (recompute() === 0) return undefined;
    const interval = window.setInterval(() => {
      if (recompute() === 0) window.clearInterval(interval);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [lastSentAt]);

  const handleRequest = useCallback(async (values: IOtpRequestFields, { setSubmitting }: FormikHelpers<IOtpRequestFields>) => {
    setSubmitError(null);
    const dest = values.identifier;

    // Honor any in-flight cooldown for this destination — jump to verify with
    // the persisted timestamp so Back+resubmit can't sidestep the throttle.
    const persisted = readLastSent(dest);
    if (persisted !== null) {
      setDestination(dest);
      setLastSentAt(persisted);
      setStep('verify');
      setSubmitting(false);
      return;
    }

    const setDateNow = () => {
      const now = Date.now();
      writeLastSent(dest, now);
      setDestination(dest);
      setLastSentAt(now);
    }

    try {
      await onRequest(dest);
      setDateNow()
      setStep('verify');
    } catch (err) {
      console.error('TwoStepOtp request failed:', err);
      if (isRateLimited(err)) {
        // Server-enforced rate limit: arm the cooldown and jump to verify so the
        // UI reflects the throttle even if the local cooldown was cleared/tampered.
        setDateNow()
        setStep('verify');
        setSubmitError('Too many code requests. Please wait a moment, then enter the code we already sent.');
      } else {
        setSubmitError(err.message || requestErrorFallback);
      }
    }
    setSubmitting(false);
  }, [onRequest, requestErrorFallback]);

  const handleVerify = useCallback(async (values: IOtpVerifyFields, helpers: FormikHelpers<IOtpVerifyFields>) => {
    setSubmitError(null);
    try {
      await onVerify(destination, values.code);
    } catch (err) {
      console.error('TwoStepOtp verify failed:', err);
      setSubmitError(err.message || verifyErrorFallback);
      // Clear the wrong code so the next attempt starts empty.
      helpers.setFieldValue('code', '');
    }
    helpers.setSubmitting(false);
  }, [destination, onVerify, verifyErrorFallback]);

  // The form component clears the code field before invoking this — keeps
  // the resend-clear reliable even before the first verify submission.
  const handleResend = useCallback(async () => {
    if (resendSecondsRemaining > 0 || resending || !destination) return;
    setSubmitError(null);
    setResending(true);
    try {
      await onRequest(destination);
      const now = Date.now();
      writeLastSent(destination, now);
      setLastSentAt(now);
    } catch (err) {
      console.error('TwoStepOtp resend failed:', err);
      if (isRateLimited(err)) {
        // Re-arm the cooldown from the server's signal (see handleRequest).
        const now = Date.now();
        writeLastSent(destination, now);
        setLastSentAt(now);
        setSubmitError('Too many code requests. Please wait a moment before trying again.');
      } else {
        setSubmitError(err.message || requestErrorFallback);
      }
    }
    setResending(false);
  }, [destination, onRequest, requestErrorFallback, resendSecondsRemaining, resending]);

  // Reset transient UI flags so the next verify mount isn't stuck; never
  // clear the persisted cooldown — that's what blocks Back+resubmit bypass.
  const onBack = useCallback(() => {
    setSubmitError(null);
    setStep('request');
    setLastSentAt(null);
    setResending(false);
  }, []);

  return {
    step,
    destination,
    submitError,
    handleRequest,
    handleVerify,
    onBack,
    resendSecondsRemaining,
    resending,
    handleResend,
  };
};
